import { createHttpEndpoint } from '../../utils'

import type {
  GetContactsMetadata,
  GetDsaContactDetails,
  GetEprInformation,
  GetLegalDetailsMetadata,
  GetLegalDocuments,
  GetLegalDocumentsItem,
  GetLegalDocumentsMetadata,
  GetProfile,
  GetProfileMetadata,
  GetProfileView,
  GetTaxIdentificationNumbers,
  GetTaxResidenceCountries,
  GetVatInformations,
} from './merchant-profile-page.types'

/**
 * Get profile for authenticated merchant
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile}
 */
export const getProfile = <T extends GetProfileView>() =>
  createHttpEndpoint<GetProfile<T>>({
    method: 'GET',
    operationId: 'getSellerProfile',
    path: '/bm/merchants/profile',
  })

export const getProfileViewAddress = getProfile<'address'>()
export const getProfileViewLanguage = getProfile<'language'>()
export const getProfileViewContact = getProfile<'contacts'>()
export const getProfileViewLegalDetails = getProfile<'legalDetails'>()
export const getProfileViewBankingDetails = getProfile<'bankingInformation'>()

/**
 * Update profile for the authenticated merchant
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/put-bm-merchants-profile}
 */
export const updateProfile = createHttpEndpoint({
  method: 'PUT',
  operationId: 'updateSellerProfile',
  path: '/bm/merchants/profile',
})

/**
 * Get metadata for main profile page
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-metadata}
 */
export const getProfileMetadata = createHttpEndpoint<GetProfileMetadata>({
  method: 'GET',
  operationId: 'getSellerProfileMetadata',
  path: '/bm/merchants/profile/metadata',
})

/**
 * Get metadata for contacts profile page
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-contacts-metadata}
 */
export const getContactsMetadata = createHttpEndpoint<GetContactsMetadata>({
  method: 'GET',
  operationId: 'getSellerProfileContactsMetadata',
  path: '/bm/merchants/profile/contacts/metadata',
})

/**
 * Get metadata for legal details profile page
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-legal-details-metadata}
 */
export const getLegalDetailsMetadata =
  createHttpEndpoint<GetLegalDetailsMetadata>({
    method: 'GET',
    operationId: 'getSellerProfileLegalDetailsMetadata',
    path: '/bm/merchants/profile/legal-details/metadata',
  })

/**
 * Get seller's EPR information.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-legal-details-epr-information}
 */
export const getEprInformation = createHttpEndpoint<GetEprInformation>({
  method: 'GET',
  operationId: 'getSellerProfileEprInformation',
  path: '/bm/merchants/profile/legal-details/epr-information',
})

/**
 * Add seller's EPR information for a market and a category.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/post-bm-merchants-profile-legal-details-epr-information}
 */
export const postEprInformation = createHttpEndpoint({
  method: 'POST',
  operationId: 'addSellerProfileEprInformation',
  path: '/bm/merchants/profile/legal-details/epr-information',
})

/**
 * Delete a single seller's EPR information.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/delete-bm-merchants-profile-legal-details-epr-information}
 */
export const deleteEprInformation = createHttpEndpoint({
  method: 'DELETE',
  operationId: 'deleteSellerProfileEprInformation',
  path: '/bm/merchants/profile/legal-details/epr-information/:id',
})

/**
 * Get seller's VAT informations.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-legal-details-vat-informations}
 */
export const getVatInformations = createHttpEndpoint<GetVatInformations>({
  method: 'GET',
  operationId: 'getSellerProfileVatInformations',
  path: '/bm/merchants/profile/legal-details/vat-informations',
  transformResponseToCamelCase: true,
})

/**
 * Save seller's VAT informations.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/put-bm-merchants-profile-legal-details-vat-informations}
 */
export const putVatInformations = createHttpEndpoint({
  method: 'PUT',
  operationId: 'updateSellerProfileVatInformations',
  path: '/bm/merchants/profile/legal-details/vat-informations',
})

/**
 * Get metadata for documents profile page
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-documents-metadata}
 */
export const getLegalDocumentsMetadata =
  createHttpEndpoint<GetLegalDocumentsMetadata>({
    method: 'GET',
    operationId: 'getSellerProfileDocumentsMetadata',
    path: '/bm/merchants/profile/legal-documents/metadata',
  })

/**
 * Get documents of a merchant
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-legal-documents}
 */
export const getLegalDocuments = createHttpEndpoint<GetLegalDocuments>({
  method: 'GET',
  operationId: 'getSellerProfileLegalDocuments',
  path: '/bm/merchants/profile/legal-documents',
})

/**
 * Post document for a merchant
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/post-bm-merchants-profile-legal-documents}
 */
export const postLegalDocument = createHttpEndpoint<GetLegalDocumentsItem>({
  method: 'POST',
  operationId: 'addSellerProfileLegalDocument',
  path: '/bm/merchants/profile/legal-documents',
})

/**
 * Delete document of a merchant
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/delete-bm-merchants-profile-legal-documents}
 */
export const deleteLegalDocument = createHttpEndpoint({
  method: 'DELETE',
  operationId: 'deleteSellerProfileLegalDocument',
  path: '/bm/merchants/profile/legal-documents',
})

/**
 * Get seller's Tax Residence Countries.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-legal-details-vat-informations}
 */
export const getTaxResidenceCountries =
  createHttpEndpoint<GetTaxResidenceCountries>({
    method: 'GET',
    operationId: 'getSellerTaxResidenceCountries',
    path: '/bm/merchants/profile/legal-details/tax-information',
  })

/**
 * Update seller's Tax Residence Countries.
 *
 * @see {@link https://devportal.backmarket.io/catalog/default/api/seller-profile-api/definition#tag/Merchant-profile-page/operation/get-bm-merchants-profile-legal-details-vat-informations}
 */
export const updateTaxResidenceCountries = createHttpEndpoint({
  method: 'PUT',
  operationId: 'updateSellerTaxResidenceCountries',
  path: '/bm/merchants/profile/legal-details/tax-information',
})

export const getTaxIdentificationNumbers =
  createHttpEndpoint<GetTaxIdentificationNumbers>({
    method: 'GET',
    operationId: 'getSellerTaxIdentificationNumbers',
    path: '/bm/merchants/profile/legal-details/tax-identification-numbers',
  })

export const updateTaxIdentificationNumbers = createHttpEndpoint({
  method: 'PUT',
  operationId: 'updateSellerTaxIdentificationNumbers',
  path: '/bm/merchants/profile/legal-details/tax-identification-numbers/:tinId',
})

export const createTaxIdentificationNumber = createHttpEndpoint({
  method: 'POST',
  operationId: 'createSellerTaxIdentificationNumber',
  path: '/bm/merchants/profile/legal-details/tax-identification-numbers',
})

export const deleteTaxIdentificationNumber = createHttpEndpoint({
  method: 'DELETE',
  operationId: 'updateSellerTaxIdentificationNumber',
  path: '/bm/merchants/profile/legal-details/tax-identification-numbers/:tinId',
})

export const getDsaContactDetails = createHttpEndpoint<GetDsaContactDetails>({
  method: 'GET',
  operationId: 'getSellerDsaContactDetails',
  path: '/api/seller-experience/legal-details/dsa/contact-details',
})

export const postDsaContactDetails = createHttpEndpoint({
  method: 'POST',
  operationId: 'postSellerDsaContactDetails',
  path: '/api/seller-experience/legal-details/dsa/contact-details',
})

export const bulkUpdateDsaContactDetails = createHttpEndpoint({
  method: 'POST',
  operationId: 'bulkUpdateSellerDsaContactDetails',
  path: '/api/seller-experience/legal-details/dsa/contact-details/bulk/import',
})
